import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useOfferTags } from 'cityads-v3/hooks/offers'
import { useScrollToTop } from 'cityads-v3/hooks/utils';
import { useThemeChanger } from "cityads-v3/hooks/theme";
import { usePaginatorActions, useOfferFilters, useClearFilters, OfferTabValue, useOfferFiltersProps } from './OfferFilters.hooks'
import { amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';
import { breakpoints, leftMenuLargeWidth } from 'cityads-v3/utils/styleUtils';
import { useViewSelector } from 'cityads-v3/components/ViewSelector/ViewSelector'

import {
  clearOfferList, getFilterCount, getOffers, getOffersExportEndpoint,
} from 'cityads-v3/actions/offers';
import { RootState } from "cityads-v3/types/reducer";

type useOffersProps = {
  defaultFilters?: useOfferFiltersProps['defaultFilters'],
  embeddedUrlAdditional?: string,
  tabValue: OfferTabValue,
  linkSource?: string,
  totalText?: string,
}

export function useOffers(props: useOffersProps) {
  const dispatch = useDispatch()
  const themeChanger = useThemeChanger()
  const { t } = useTranslation(['Offers'])
  const [offerTags] = useOfferTags();
  const { setPageLimit, setPage } = usePaginatorActions();
  const offerFilters = useOfferFilters({
    defaultFilters: props.defaultFilters,
    embeddedUrlAdditional: props.embeddedUrlAdditional,
    tabValue: props.tabValue,
    onSubmit: useCallback((filters) => {
      if (!props.linkSource) {
        amplitudeSendEvent('makeActions', 'd-v1-e51', 'offers', 'filter_apply', {
          is_region: filters.region_id?.length > 0,
          is_offer_type: filters.direction_and_platform?.length > 0,
          is_status: filters.subscription_status?.length > 0,
          is_category: filters.category_id?.length > 0,
          is_traffic_type: filters.traffic_type_id?.length > 0,
          is_tools: filters.tool_id?.length > 0,
          has_promocode_attribution: filters.has_promocode_attribution?.length > 0,
          is_deeplink_enabled: filters.is_deeplink_enabled?.length > 0,
          has_cart: filters.has_cart?.length > 0,
        })
      }
    }, [props.linkSource])
  })
  const clearFilters = useClearFilters(offerFilters.selectedFilters);
  const scrollToTop = useScrollToTop();
  const [view, setView] = useViewSelector('offersListView', 'list');

  const withFilters = useSelector((state: RootState) => state.offers.meta.withFilters);
  const isLoadingOffers = useSelector((state: RootState) => state.offers.isLoadingOffers);
  const total = useSelector((state: RootState) => state.offers.meta.total);

  const totalText = useMemo(() => {
    if (props.totalText) {
      return props.totalText;
    }

    if (withFilters && !isLoadingOffers) {
      return t('totalText', { count: total });
    }

    return null;
  }, [
    props.totalText,
    withFilters,
    isLoadingOffers,
    t,
    total
  ])

  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dispatch(getFilterCount({ filter: {} }, { total: true }));

    return () => {
      dispatch(clearOfferList())
    }
  }, [dispatch])

  return {
    clearFilters,
    listRef,
    offerFilters,
    offerTags,
    setPage,
    setPageLimit,
    scrollToTop,
    view,
    setView,
    themeChanger,
    totalText,
  }
}

/**
 * Получение ссылки для экспорта списка офферов
 */
type ExportType = 'xlsx' | 'csv'
export function useExportData(offerFilters: ReturnType<typeof useOfferFilters>, embeddedUrlAdditional?: string) {
  return useCallback((type: ExportType) => {
    const endpoint = getOffersExportEndpoint({ type })
    const filter = offerFilters.selectedFilters.getFormData();
    if (!filter.has_cart) { delete filter.has_cart }
    if (!filter.has_promocode_attribution) { delete filter.has_promocode_attribution }
    if (!filter.is_deeplink_enabled) { delete filter.is_deeplink_enabled }

    const url = getOffers({
      returnAction: true,
      url: endpoint,
      page: 0,
      additional: embeddedUrlAdditional ? embeddedUrlAdditional : 'priority_sort=true',
      filter
    }) as string;

    if (window) {
      window.open(url);
    }

  }, [embeddedUrlAdditional, offerFilters.selectedFilters])
}

/**
 * Вывод кнопок экспорта офферов
 */
export const useExportsConfigOffers = (
  offerFilters: ReturnType<typeof useOfferFilters>,
  embeddedUrlAdditional?: string
) => {
  const { t } = useTranslation(['main'])
  const handleExportData = useExportData(offerFilters, embeddedUrlAdditional)
  return useMemo(() => ({
    title: t('buttons.export'),
    items: [
      {
        title: 'xls',
        icon: 'file_download',
        onClick: () => {
          amplitudeSendEvent('makeActions', 'd-v1-e38', 'offers', 'export', { export_type: 'xls' });
          handleExportData('xlsx')
        }
      },
      {
        title: 'csv',
        icon: 'file_download',
        onClick: () => {
          amplitudeSendEvent('makeActions', 'd-v1-e38', 'offers', 'export', { export_type: 'csv' });
          handleExportData('csv')
        }
      }
    ]
  }), [t, handleExportData])
}


/**
 * Описание возможных сортировок
 */
export const useOrderKeys = () => {
  const { t } = useTranslation(['Offers']);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn)

  return useMemo(() => ([
    {
      name: 'id',
      title: t('table.id'),
      defaultAsc: true,
    },
    {
      name: 'name',
      title: t('table.name'),
      defaultAsc: true,
    },
    {
      name: 'date_start',
      title: t('table.date_start'),
      defaultAsc: false,
    },
    {
      name: 'cr',
      title: t('table.cr'),
      defaultAsc: false,
    },
    {
      name: 'ar',
      title: t('table.ar'),
      defaultAsc: false,
    },
    {
      name: 'ecpc_ecpl',
      title: t('table.ecpc_ecpl'),
      defaultAsc: false,
    },
    {
      name: 'approval_time',
      title: t('table.approval_time'),
      defaultAsc: true,
    },
    {
      name: 'cookie_lifetime',
      title: t('table.cookie_lifetime'),
      defaultAsc: true,
    },
    ...(isLoggedIn ? [{
      name: 'subscription_status',
      title: t('table.subscription_status'),
      defaultAsc: true,
    }] : [])
  ]), [isLoggedIn, t])
}


/**
 * Подгрузка количества офферов в избранном
 */
export function useFavoriteCount() {
  const dispatch = useDispatch()
  const total = useSelector((state: RootState) => state.offers.favoriteOffersGlobalTotal)

  const load = useCallback(() => {
    dispatch(getFilterCount({ filter: { is_favorite: true } }, { total: true }));
  }, [dispatch])

  useEffect(load, [load]);

  return [total, load];
}


/**
 * @deprecated лучше использовать css container queries
 */
export function useDoubleColumnLimit() {
  const isMenuDocked = useSelector((state: RootState) => state.menu.docked)

  return useMemo(() => (
    isMenuDocked ?
      breakpoints.sm + parseInt(leftMenuLargeWidth, 10) :
      breakpoints.sm
  ), [isMenuDocked])
}


export enum OfferFilterType {
  ALL = 0,
  FAVORITES = 1
}
